import { useMemo, useState } from 'react';
import { Empty, Flex, Input, Skeleton, theme, Tree } from 'antd';
import { FiSearch } from 'react-icons/fi';
import styled from 'styled-components';

import { collectUuids, filterObjectTree } from '../objectTree';
import { useObjects } from '@hooks/useObjects';
import ObjectTreeCard from './ObjectTreeCard';

// Card rows are taller than antd's single-line nodes: center the caret on the
// card and space the rows apart. Tied to antd v6 class names — review on upgrades.
const TreeWrapper = styled.div`
	.ant-tree-treenode {
		margin-bottom: ${({ $gap }) => $gap}px;
	}

	.ant-tree-switcher {
		align-self: center;
	}
`;

// Nodes are identified by uuid; the backend `key` is a slug, not the tree id.
const TREE_FIELD_NAMES = { key: 'uuid', title: 'name' };

const ListMessage = ({ children }) => (
	<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={children} />
);

const ObjectList = ({ mode, selectedUuid = null, onSelect, onEdit, onDelete }) => {
	const { token } = theme.useToken();
	const { tree, isLoading, isError } = useObjects(mode);

	const [query, setQuery] = useState('');
	// null = no manual expand/collapse yet → everything expanded.
	const [expandedKeys, setExpandedKeys] = useState(null);
	// Expand state within one search; a new query starts fully expanded again.
	const [searchKeys, setSearchKeys] = useState(null);

	const trimmedQuery = query.trim();
	const isSearching = trimmedQuery.length > 0;

	const visibleTree = useMemo(() => filterObjectTree(tree, trimmedQuery), [tree, trimmedQuery]);
	const allKeys = useMemo(() => collectUuids(visibleTree), [visibleTree]);

	// A search reveals every match, kept separate from the browsing state so
	// clearing the search restores what the user had open.
	const effectiveExpandedKeys = (isSearching ? searchKeys : expandedKeys) ?? allKeys;

	const handleQueryChange = event => {
		setQuery(event.target.value);
		setSearchKeys(null);
	};

	const handleExpand = keys => (isSearching ? setSearchKeys(keys) : setExpandedKeys(keys));

	// Clicking the selected row again keeps it selected rather than clearing it.
	const handleSelect = (_keys, { node }) => onSelect?.(node.uuid);

	const renderTitle = node => (
		<ObjectTreeCard
			object={node}
			mode={mode}
			selected={node.uuid === selectedUuid}
			onEdit={onEdit}
			onDelete={onDelete}
		/>
	);

	const renderBody = () => {
		if (isLoading) return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
		if (isError)
			return <ListMessage>Couldn&apos;t load objects. Please try again.</ListMessage>;
		if (tree.length === 0) return <ListMessage>No objects yet.</ListMessage>;
		if (visibleTree.length === 0)
			return <ListMessage>No objects match this search.</ListMessage>;

		return (
			<TreeWrapper $gap={token.marginXS}>
				<Tree
					blockNode
					fieldNames={TREE_FIELD_NAMES}
					treeData={visibleTree}
					titleRender={renderTitle}
					expandedKeys={effectiveExpandedKeys}
					onExpand={handleExpand}
					selectedKeys={selectedUuid ? [selectedUuid] : []}
					onSelect={handleSelect}
				/>
			</TreeWrapper>
		);
	};

	return (
		<Flex vertical gap="small">
			<Input
				allowClear
				value={query}
				onChange={handleQueryChange}
				placeholder="Search objects or aliases"
				aria-label="Search objects or aliases"
				prefix={<FiSearch aria-hidden color={token.colorTextTertiary} />}
			/>
			{renderBody()}
		</Flex>
	);
};

export default ObjectList;
